/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { UserProfile } from '@/types/auth';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/supabase-auth';
import { CircuitBreaker } from '@/lib/circuitBreaker';
import { fetchProfileWithFallbacks, getFallbackMessage } from '@/lib/profileFallbacks';
import { cacheProfile } from '@/lib/profileCache';
import { supabase } from '@/integrations/supabase/client';
import { getErrorMessage } from '@/types/common';
import { safeConsoleError, safeConsoleWarn } from '@/lib/errorSanitizer';

interface ProfileContextType {
  profile: UserProfile | null;
  refreshProfile: () => Promise<void>;
  loading: boolean;
}

const ProfileContext = createContext<ProfileContextType | null>(null);

// Circuit breaker for profile loading - prevents cascading failures
const profileCircuitBreaker = new CircuitBreaker({
  failureThreshold: 3,
  timeout: 30000, // 30s
  name: 'ProfileLoader',
});

// Circuit breaker for audit logging - prevents audit log spam
const auditCircuitBreaker = new CircuitBreaker({
  failureThreshold: 5,
  timeout: 60000, // 1 min
  name: 'AuditLogger',
});

/**
 * Non-blocking audit logging for profile access
 * ✅ PII-FREE: Only logs metadata, no sensitive data
 * ✅ RATE LIMITED: Circuit breaker prevents spam
 * ✅ NON-BLOCKING: Failures don't affect user experience
 */
async function logProfileAccess(profile: UserProfile, source: string) {
  try {
    // Avoid noisy 401s if auth token isn't attached yet (or user is logged out)
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return;
    }

    // Circuit breaker prevents spam
    if (auditCircuitBreaker.getState().state === 'OPEN') {
      console.debug('[Audit] Circuit breaker open, skipping log');
      return;
    }

    await auditCircuitBreaker.execute(async () => {
      const { error } = await supabase.from('audit_events').insert({
        user_id: profile.user_id,
        actor_user_id: profile.user_id,
        event_type: 'profile_accessed',
        event_data: {
          source,
          timestamp: new Date().toISOString(),
          // ✅ NO PII: No email, name, phone, address, or PHI
        },
        entity_type: 'profile',
        entity_id: profile.user_id,
        organization_id: profile.organization_id,
      });

      if (error) {
        // SECURITY: Use sanitized warning, not full error object
        safeConsoleWarn('Audit', 'Non-blocking log failure');
      }
    });
  } catch (error) {
    // Non-blocking: audit failures don't block user experience
    console.debug('[Audit] Skipped due to circuit breaker or error');
  }
}

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!session?.user?.id) {
        console.debug('[ProfileProvider] No session - clearing profile');
        setProfile(null);
        setLoading(false);
        return;
      }

      // Check for access token
      if (!session?.access_token) {
        console.warn('[ProfileProvider] Session exists but no access_token');
        setLoading(false);
        return;
      }

      // Execute with circuit breaker protection
      await profileCircuitBreaker.execute(async () => {
        console.debug('[ProfileProvider] Fetching profile with fallback hierarchy...');
        
        const result = await fetchProfileWithFallbacks(session);
        
        // Handle result based on source
        if (result.profile) {
          setProfile(result.profile);
          
          // Cache successful fetches from RPC
          if (result.source === 'rpc_secure') {
            cacheProfile(result.profile);
          }
          // ✅ NON-BLOCKING AUDIT: Log profile access (moved from DB function)
          logProfileAccess(result.profile, result.source).catch(err => {
            console.debug('[ProfileProvider] Audit log skipped:', err);
          });
          
          // Show toast for fallback sources
          const message = getFallbackMessage(result.source);
          if (message) {
            toast({
              title: 'Profile loaded from fallback',
              description: message,
              variant: 'default',
            });
          }
          
          console.debug(`[ProfileProvider] ✅ Profile loaded from: ${result.source}`);
        } else {
          console.warn('[ProfileProvider] Failed to load profile from any source');
          setProfile(null);
        }
        
        setLoading(false);
      });
      
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      
      // Circuit breaker is OPEN
      if (errorMessage.includes('Circuit breaker is OPEN')) {
        // SECURITY: Use sanitized logging
        safeConsoleError('ProfileProvider', 'Circuit breaker OPEN - using fallback');
        
        // Show user-friendly message
        toast({
          title: 'Profile service temporarily unavailable',
          description: 'Using cached data. Will retry automatically in 30 seconds.',
          variant: 'default',
        });
        
        // Try to load from cache or session
        const result = await fetchProfileWithFallbacks(session);
        if (result.profile) {
          setProfile(result.profile);
        }
      } else {
        // SECURITY: Sanitize error before logging
        safeConsoleError('ProfileProvider', error);
      }
      
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    let mounted = true;
    
    const loadProfile = async () => {
      if (mounted) {
        await refreshProfile();
      }
    };
    
    loadProfile();
    
    return () => {
      mounted = false;
    };
  }, [refreshProfile]); // React to session changes

  return (
    <ProfileContext.Provider value={{ profile, refreshProfile, loading }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}
